import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { RQAPICallWithData } from 'store/APIClient';
import { IntegrationT } from 'routes/IntegrationsList/types';
import { useCallback } from 'react';
import { mantyIntegrations } from 'routes/IntegrationsList/fullIntegrationList';

export const useGetIntegrations = <T = IntegrationT[]>(
    // The select option can be used to transform the data returned by the endpoint
    options?: Pick<UseQueryOptions<IntegrationT[], unknown, T>, 'select'>,
) =>
    useQuery({
        queryKey: ['integrations'],

        queryFn: async () => {
            const response = await RQAPICallWithData<IntegrationT[]>({
                endpoint: 'integrations',
                method: 'GET',
            });
            return response.content.data;
        },

        // an hour
        staleTime: 3600 * 1000,

        select: options?.select,
    });

export const useIntegrationFunctions = () => {
    const select = useCallback(
        (data: IntegrationT[]) => new Set(data.map((i) => i.function)),
        [],
    );
    return useGetIntegrations({
        select,
    });
};

export const useIntegrationSoftwares = () => {
    const select = useCallback(
        (data: IntegrationT[]) => new Set(data.map((i) => i.software)),
        [],
    );
    return useGetIntegrations({
        select,
    });
};

export const useHrIntegrationSoftwares = () => {
    const select = useCallback(
        (data: IntegrationT[]) =>
            new Set(
                data
                    .filter((i) => i.isActive && i.function === 'hr')
                    .map((i) => i.software),
            ),
        [],
    );
    return useGetIntegrations({
        select,
    });
};

/**
 * Custom hook to get full integrations by merging the list of integrations and integrations for the bind.
 *
 * @returns - The full list of integrations and those added.
 */
export const useGetFullIntegrations = () => {
    const select = useCallback(
        (data: IntegrationT[]) =>
            mantyIntegrations.map((integration) => {
                const apiIntegration = data.find(
                    (i) =>
                        i.software.toLowerCase() ===
                        integration.software.toLowerCase(),
                );
                return apiIntegration
                    ? { ...integration, ...apiIntegration, isAdded: true }
                    : (integration as IntegrationT);
            }),
        [],
    );
    return useGetIntegrations({ select });
};
